import { useEffect, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { referralsService } from '@/services/referralsService'
import { ParceirosBrandShell } from '@/pages/parceiros/ParceirosBrandShell'
import { ParceiroTermosAceite } from '@/pages/parceiros/ParceiroTermosAceite'
import { readParceiroTermosNavegador } from '@/pages/parceiros/parceiroTermosNavegador'
import {
  PARCEIROS_ACCENT,
  PARCEIROS_LOGIN_PATH,
  fieldClass,
  labelClass,
} from '@/pages/parceiros/parceirosBrand'

type Cadastro = {
  nome: string
  empresa: string | null
  email: string
  telefone: string
  cnpj: string | null
  responsavel: string | null
  telefoneContato: string | null
}

export default function PortalParceiroConfirmar() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [cadastro, setCadastro] = useState<Cadastro | null>(null)
  const [aceitouTermos, setAceitouTermos] = useState(false)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState('')

  useEffect(() => {
    if (token.length < 32) {
      setError('Link inválido ou expirado.')
      setLoading(false)
      return
    }
    referralsService.previewConfirmacao(token)
      .then((data) => setCadastro(data))
      .catch(() => setError('Link inválido ou expirado.'))
      .finally(() => setLoading(false))
  }, [token])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!cadastro || !aceitouTermos) return
    setSubmitting(true)
    setError('')
    try {
      const navegador = await readParceiroTermosNavegador()
      const result = await referralsService.confirmarCadastro({
        token,
        nome: cadastro.nome,
        empresa: cadastro.empresa ?? undefined,
        telefone: cadastro.telefone,
        cnpj: cadastro.cnpj ?? undefined,
        responsavel: cadastro.responsavel ?? undefined,
        telefoneContato: cadastro.telefoneContato ?? undefined,
        aceitouTermos: true,
        ...navegador,
      })
      setDone(result?.data?.message ?? 'Cadastro confirmado.')
    } catch {
      setError('Não foi possível confirmar o cadastro. O link pode ter expirado.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ParceirosBrandShell
      rightSlot={
        <Link to={PARCEIROS_LOGIN_PATH} className='rounded-full px-4 py-2 text-sm font-medium text-white' style={{ background: PARCEIROS_ACCENT }}>
          Já sou parceiro
        </Link>
      }
    >
      <div className='mx-auto grid max-w-lg gap-6'>
        <div>
          <p className='text-[11px] font-medium uppercase tracking-[0.28em] text-white/55'>Programa de indicação</p>
          <h1 className='mt-2 text-3xl font-black'>Confirme seu cadastro</h1>
          <p className='mt-2 text-white/70'>Revise os dados, leia as regras e registre sua concordância.</p>
        </div>
        {done ? (
          <div className='rounded-3xl border border-[#e91e63]/30 bg-[#e91e63]/10 p-6 text-center'>
            <h2 className='text-2xl font-black'>Tudo certo por aqui.</h2>
            <p className='mt-3 text-sm leading-6 text-white/70'>{done}</p>
          </div>
        ) : loading ? (
          <p className='text-sm text-white/70'>Carregando seus dados…</p>
        ) : !cadastro ? (
          <p className='text-sm text-red-300'>{error}</p>
        ) : (
          <form onSubmit={submit} className='grid gap-4 rounded-3xl border border-white/10 bg-white/[0.04] p-6'>
            <label className='grid gap-1.5'>
              <span className={labelClass}>Seu nome</span>
              <input className={fieldClass} value={cadastro.nome} onChange={(e) => setCadastro({ ...cadastro, nome: e.target.value })} required minLength={2} />
            </label>
            <label className='grid gap-1.5'>
              <span className={labelClass}>Empresa</span>
              <input className={fieldClass} value={cadastro.empresa ?? ''} onChange={(e) => setCadastro({ ...cadastro, empresa: e.target.value })} />
            </label>
            <label className='grid gap-1.5'>
              <span className={labelClass}>E-mail</span>
              <input className={fieldClass} value={cadastro.email} readOnly />
            </label>
            <label className='grid gap-1.5'>
              <span className={labelClass}>Telefone</span>
              <input className={fieldClass} value={cadastro.telefone} onChange={(e) => setCadastro({ ...cadastro, telefone: e.target.value })} required minLength={8} />
            </label>
            <p className='text-xs leading-5 text-white/55'>
              Ao confirmar, guardamos a data, o IP, o navegador, o idioma, o fuso e, se você permitir, a localização aproximada deste aparelho.
            </p>
            <ParceiroTermosAceite checked={aceitouTermos} onChange={setAceitouTermos} />
            {error && <p className='text-sm text-red-300'>{error}</p>}
            <button
              type='submit'
              disabled={submitting || !aceitouTermos}
              className='rounded-full px-5 py-3 text-sm font-medium text-white disabled:opacity-50'
              style={{ background: PARCEIROS_ACCENT }}
            >
              {submitting ? 'Confirmando…' : 'Confirmar cadastro'}
            </button>
          </form>
        )}
      </div>
    </ParceirosBrandShell>
  )
}
